import { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { alpha, type Theme } from '@mui/material/styles';
import { AppBar, Avatar, Grid, InputBase, Link, List, ListItem, ListItemAvatar, ListItemText, Paper, Toolbar, Typography } from '@mui/material';
import FileIcon from '@mui/icons-material/AttachFile';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import { useDownloadResourceStore, usePartnersStore, useProductsStore, useRepositoryStore } from '@lib';
import { FolderEntity, FileEntity } from '@shared/api';
import { DelayedLinearProgress } from '@widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  appBar: {
    marginBottom: theme.spacing(2),
  },
  title: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 7),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 200,
    },
  },
  emptyFolder: {
    textAlign: 'center',
    padding: theme.spacing(5),
  },
  searchMessage: {
    padding: theme.spacing(2, 1, 2, 1),
  },
  select: {
    '&:before': {
      borderColor: `${theme.palette.primary.contrastText}`,
    },
    '&:hover:not(.Mui-disabled):before': {
      borderColor: `${theme.palette.primary.contrastText}`,
    },
    color: theme.palette.primary.contrastText,
  },
  selectIcon: {
    fill: theme.palette.primary.contrastText,
    color: theme.palette.primary.contrastText,
  },
}));

export const RepositoryPage = () => {
  const partners = usePartnersStore((state) => state.partners);
  const { loading: productsLoading, products, getProducts } = useProductsStore();
  const { loading, folder: resourceFolderData, getRepository } = useRepositoryStore();
  const { downloadResource } = useDownloadResourceStore();

  const [resourcesData, setResourcesData] = useState<FileEntity[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<FolderEntity | undefined>();
  const [searchText, setSearchText] = useState<string | undefined>();

  const classes = useStyles();

  useEffect(() => {
    getProducts();
    getRepository();
  }, []);

  useEffect(() => {
    if (selectedFolder && selectedFolder.files?.length) {
      if (searchText && searchText.length > 0)
        setResourcesData(selectedFolder!.files!.filter((item) => item.fileName.toLowerCase().indexOf(searchText!.toLowerCase()) >= 0));
      else setResourcesData(selectedFolder.files!);
    } else {
      setResourcesData([]);
    }
  }, [selectedFolder, searchText]);

  // const onPartnerChange = (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
  //   if (typeof e.target.value === 'string') {
  //     localStorage.setItem('selectedPartnerId', e.target.value);
  //     setSelectedPartnerState(e.target.value);
  //   }
  // };

  return (
    <>
      <DelayedLinearProgress loading={loading || productsLoading} />
      <AppBar position='static' className={classes.appBar}>
        <Toolbar>
          {!selectedFolder && <Typography variant='body1'>Start by selecting a folder.</Typography>}
          {selectedFolder && (
            <Typography className={classes.title} variant='h6' noWrap>
              {selectedFolder.folderName}
            </Typography>
          )}
          {selectedFolder && (
            <div className={classes.search}>
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder='Search…'
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          )}
        </Toolbar>
      </AppBar>

      <DelayedLinearProgress loading={loading || productsLoading} />
      {!loading && !productsLoading && resourceFolderData && products && partners && (
        <Paper>
          <Grid container alignContent='center'>
            <Grid item xs={2}>
              <List>
                {resourceFolderData.map((folder, idx) => (
                  <ListItem key={idx} selected={selectedFolder && selectedFolder === folder} button onClick={() => setSelectedFolder(folder)}>
                    <ListItemAvatar>
                      <Avatar>
                        <FolderIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={folder.folderName} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={10}>
              {searchText && searchText.length > 0 && (
                <Typography className={classes.searchMessage}>
                  Showing {resourcesData.length} file
                  {resourcesData.length !== 1 && 's'} matching &apos;{searchText}&apos;.{' '}
                  <Link variant='body1' onClick={() => setSearchText('')}>
                    Clear search.
                  </Link>
                </Typography>
              )}
              {resourcesData.length ? (
                <List>
                  {resourcesData.map((resource, idx) => {
                    return (
                      <ListItem key={idx} button onClick={() => downloadResource({ id: resource.id, filename: resource.fileName })}>
                        <ListItemAvatar>
                          <Avatar>
                            <FileIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={resource.fileName} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <div className={classes.emptyFolder}>
                  {selectedFolder && resourcesData.length === 0 && (
                    <Typography variant={'body1'}>There are no files matching your search.</Typography>
                  )}
                </div>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
};
